import RangeLimits from '@scripts/components/RangeLimits';
import FormPartial from '@scripts/forms/FormPartial';
import CvRiskHelpers from '@scripts/helpers';

/**
 * "Current labs/exams" form part
 */
export default class extends FormPartial {
  /**
   * If the components are being refreshed
   *
   * @private
   *
   * @type {bool}
   */
  #isRefresh = false;

  /**
   * Labs data used for conversions
   *
   * @private
   *
   * @type {object}
   */
  #labsData;

  /**
   * Unit switcher for cholesterol
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #chUnitSwitch;

  /**
   * Systolic blood pressure
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #sbp;

  /**
   * Diastolic blood pressure
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #dbp;

  /**
   * Cholesterol Unit
   *
   * @private
   *
   * @type {string}
   */
  #chUnit;

  /**
   * Total Cholesterol
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #tc;

  /**
   * HDL Cholesterol
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #hdl;

  /**
   * Non-HDL Cholesterol
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #nonHdl;

  /**
   * Non-HDL Cholesterol
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #nonHdlValue;

  /**
   * LDL Cholesterol
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #ldl;

  /**
   * Triglyceride
   *
   * @private
   *
   * @type {RangeLimits}
   */
  #tg;

  constructor(form) {
    super(form);

    this.#isRefresh = false;
    this.#labsData = window?.CvRisk?.lab?.data || {};
    this.#chUnitSwitch = this._form.element.querySelector(
      '#cvriskLabChUnitSwitch'
    );
    this.#chUnit = this.#chUnitSwitch.querySelector('input:checked').value;
    this.#sbp = new RangeLimits(
      this._form.element.querySelector('#cvriskLabSbp')
    );
    this.#dbp = new RangeLimits(
      this._form.element.querySelector('#cvriskLabDbp')
    );
    this.#tc = new RangeLimits(
      this._form.element.querySelector('#cvriskLabTc')
    );
    this.#hdl = new RangeLimits(
      this._form.element.querySelector('#cvriskLabHdl')
    );
    this.#nonHdl = this._form.element.querySelector('#cvriskLabNonHdlResult');
    this.#ldl = new RangeLimits(
      this._form.element.querySelector('#cvriskLabLdl')
    );
    this.#tg = new RangeLimits(
      this._form.element.querySelector('#cvriskLabTg')
    );
  }

  /**
   * Handler for changes of the blood pressure
   * - display error message if invalid
   * - trigger event to update ASCVD risk factors
   *
   * @private
   *
   * @return {void}
   */
  #handlerBloodPressureChanged() {
    // disabled during refresh
    if (this.#isRefresh) {
      return;
    }

    this._resetMessages();
    if (!this.#validateBloodPressure()) {
      this._openPopin();
    }

    // ASCVD risk factors
    this._form.dispatch('update-ascvd-risk-factors');
  }

  /**
   * Handler for changes of the cholesterols
   * - compute Non-HDL
   * - trigger event to update ASCVD risk factors
   *
   * @private
   *
   * @return {void}
   */
  #handlerCholesterolChanged() {
    this.#computeNonHdl();

    // ASCVD risk factors
    this._form.dispatch('update-ascvd-risk-factors');
  }

  /**
   * Compute of Non-HDL if the values are valid
   *
   * @private
   *
   * @return {void}
   */
  #computeNonHdl() {
    // disabled during refresh
    if (this.#isRefresh) {
      return;
    }

    // reset
    this._resetMessages();
    this.#nonHdl.innerText = '';
    this.#nonHdlValue = null;

    if (!this.#validateCholesterols()) {
      // display messages
      this._openPopin();
    } else {
      // compute Non-HDL: tc - hdl = Non-HDL
      let tc = this.#tc.getValue();
      let hdl = this.#hdl.getValue();

      this.#nonHdlValue = Number(tc - hdl).toFixed(2);
      this.#nonHdl.innerText = this.#nonHdlValue;
    }
  }

  /**
   * Convert values for cholesterols and define new ranges
   *
   * @private
   *
   * @param {string} unit Covnersion unit
   *
   * @return {void}
   */
  #convertCholesterols(unit) {
    this.#isRefresh = true;
    this.#chUnit = unit;

    [this.#tc, this.#hdl, this.#ldl, this.#tg].forEach((ch) => {
      // Convert value
      let newValue = CvRiskHelpers.unitConvert(
        this.#chUnit,
        ch.getData('type'),
        ch.getValue()
      );

      // Range limits by unit
      let limits = this.#labsData[ch.getData('key')]['units'][this.#chUnit];

      // update component with new options
      ch.updateOptions({
        start: newValue,
        step: limits.step,
        range: {
          min: limits.min,
          max: limits.max,
        },
      });
    });

    this.#isRefresh = false;

    this.#computeNonHdl();
  }

  /**
   * Validation of values for blood pressure
   *
   * @private
   *
   * @return {bool}
   */
  #validateBloodPressure() {
    let errors = [];
    let sbp = this.#sbp.getValue();
    let dbp = this.#dbp.getValue();

    // Systolic must be greater than diastolic
    if (sbp < dbp) {
      errors.push(CvRiskHelpers.error('dbp_greater_sbp'));
    }

    this.addErrors(errors);

    return errors.length === 0;
  }

  /**
   * Validation of values for cholesterols
   *
   * @private
   *
   * @return {bool}
   */
  #validateCholesterols() {
    let errors = [];
    let warnings = [];
    let hdl = this.#hdl.getValue();
    let ldl = this.#ldl.getValue();
    // Triglycerides
    let tg = this.#tg.getValue();
    // Total cholesterols
    let tc = this.#tc.getValue();

    // HDL + LDL + (Triglycerides / 5) must be lower than Total cholesterols
    if (hdl + ldl + tg / 5 > tc) {
      // Triglycerides: Mmol/L
      let tgMmol =
        this.#chUnit !== 'mmol_l'
          ? CvRiskHelpers.unitConvert('mmol_l', this.#tg.getData('type'), tg)
          : tg;
      // LDL-C: Mmol/L
      let ldlMmol =
        this.#chUnit !== 'mmol_l'
          ? CvRiskHelpers.unitConvert('mmol_l', this.#ldl.getData('type'), ldl)
          : ldl;

      // exception in case of low LDL-C and/or high TG levels
      if (tgMmol >= 4.5 || ldlMmol <= 1.3) {
        // only if the global form is being submitted
        if (this._form.beingSubmitted) {
          warnings.push(CvRiskHelpers.warning('cholesterols_values_except'));

          // error: HDL + LDL must be lower than Total cholesterols
          if (hdl + ldl > tc) {
            errors.push(CvRiskHelpers.error('wrong_cholesterols_2'));
          } else {
            this._form.withPopinSubmit = true;
          }
        }
      } else {
        errors.push(CvRiskHelpers.error('wrong_cholesterols_1'));
      }

      this.addErrors(errors);
      this.addWarnings(warnings);
    }

    return errors.length <= 0;
  }

  initialize() {
    // Blood pressures
    [this.#sbp, this.#dbp].forEach((range) =>
      range.on('updateValue', () => this.#handlerBloodPressureChanged())
    );

    // Cholesterols
    [this.#tc, this.#hdl, this.#ldl, this.#tg].forEach((range) =>
      range.on('updateValue', () => this.#handlerCholesterolChanged())
    );
    // Convert values
    this.#chUnitSwitch.addEventListener('change', (e) =>
      this.#convertCholesterols(e.target.value)
    );

    this.#computeNonHdl();
  }

  validate() {
    this._resetMessages();

    this.#validateBloodPressure();
    this.#validateCholesterols();

    return super.validate();
  }

  reset() {
    super.reset();

    this.#isRefresh = true;

    [this.#sbp, this.#dbp, this.#tc, this.#hdl, this.#ldl, this.#tg].forEach(
      (range) => range.reset()
    );

    this.#isRefresh = false;

    this.#computeNonHdl();
  }

  hasAscvdRiskFactors() {
    // Blood pressures
    let sbp = this.#sbp.getValue();
    let dbp = this.#dbp.getValue();
    if (sbp >= 120 || dbp >= 80) {
      return true;
    }

    // Cholesterol
    let ldl = this.#ldl.getValue();
    let nonHdl = this.#nonHdlValue;
    // use mmol/l unit
    if (this.#chUnit !== 'mmol_l') {
      let type = this.#ldl.getData('type');
      ldl = CvRiskHelpers.unitConvert('mmol_l', type, ldl);
      nonHdl = CvRiskHelpers.unitConvert('mmol_l', type, nonHdl);
    }

    if (ldl >= 2.6 || nonHdl >= 3.37) {
      return true;
    }

    return false;
  }
}
